import type { BreadcrumbItem } from '@//components/Breadcrumb';
import type { ElasticSearchResponseFacet } from '@//ElasticSearch';
import type { Messages, TranslatorFor } from '@//locales/messages';
import type { MarketMetadata } from '@//market';
import marketRoutesConfig from '@//marketRoutesConfig';
import { slugify } from '@//utils/slugify';

const patternToRegex = (pattern: string) => {
  const regexStr = pattern
    .replace(/\[\[\.\.\.(\w+)\]\]/g, '(/.*)?') // Handle optional catch-all "[[...slug]]"
    .replace(
      /\[(\.\.\.)?(\w+)\]/g,
      (
        _m,
        dots, // Handle "[...slug]" or "[slug]"
      ) => (dots ? '(.+)' : '([^/]+)'),
    );
  return new RegExp(`^${regexStr}$`);
};

// Finds the best-matching generic route pattern for a given localized path
const findGenericRoutePattern = (
  localizedPath: string,
  marketRoutes: Record<string, string>,
): keyof Messages['other']['breadcrumbs'] | undefined => {
  const patterns = Object.keys(marketRoutes).sort((a, b) => b.length - a.length);

  for (const pattern of patterns) {
    // Only allow matching static-to-static, or allow dynamic pattern for dynamic paths
    const isPatternDynamic = pattern.includes('[');
    const isPathDynamic = localizedPath.includes('[');
    if (!isPathDynamic && isPatternDynamic) continue; // Skip dynamic pattern for static path

    const regex = patternToRegex(pattern);
    if (regex.test(localizedPath)) {
      return marketRoutes[pattern] as keyof Messages['other']['breadcrumbs'];
    }
  }
  return undefined;
};

// Helper function to capitalize first letter of each word
const capitalizeWords = (str: string): string => {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Helper function to get correct display name from facets
// Searches across all facets to find a matching filter
const getDisplayNameFromFacets = (
  facets: ElasticSearchResponseFacet[] | undefined,
  urlSegment: string,
): string | undefined => {
  if (!facets) return undefined;

  // Decode and normalize the URL segment for comparison
  const decodedSegment = decodeURIComponent(urlSegment).toLowerCase();

  // Search through all facets to find a matching filter
  for (const facet of facets) {
    const filter = facet.filters.find((f) => {
      const slugifiedDisplayName = slugify(f.displayName.toLowerCase(), { encode: true });
      return slugifiedDisplayName === decodedSegment;
    });

    if (filter) {
      return filter.displayName;
    }
  }

  return undefined;
};

// Breadcrumbs generator returning [{ href, label }]
export const getBreadcrumbs = (
  market: keyof typeof marketRoutesConfig,
  pathname: string,
  titles: TranslatorFor<'other.breadcrumbs'>,
  marketMetadata: MarketMetadata,
  vehicleData?: { manufacturer?: string; modelSeries?: string },
  facets?: ElasticSearchResponseFacet[],
  searchSubscriptionName?: string,
): BreadcrumbItem[] => {
  const marketRoutes = marketRoutesConfig[market];
  if (!marketRoutes) return [];

  let path = pathname.replace(/\/$/, '') || '/';
  const crumbs: BreadcrumbItem[] = [];

  // Special case: For search page routes with beautiful URLs or query parameters
  const searchPath = marketMetadata.pageLinks.searchPage;
  const isSearchPage = pathname.startsWith(searchPath);

  if (isSearchPage && (market === 'wayke' || market === 'drive')) {
    const searchLabel = titles('/search');

    // Always add the root
    if (titles('/')) {
      crumbs.push({ href: '/', label: marketMetadata.brandName });
    }

    // If this is a search subscription, show: Wayke -> Köp bil -> SUBSCRIPTION_NAME
    if (searchSubscriptionName) {
      if (searchLabel) {
        crumbs.push({ href: searchPath, label: searchLabel });
      }
      crumbs.push({ href: pathname, label: searchSubscriptionName });
      return crumbs;
    }

    // Add the base search breadcrumb
    if (searchLabel) {
      crumbs.push({ href: searchPath, label: searchLabel });
    }

    // Parse beautiful URL segments (e.g., /sok/volkswagen/passat/bensin)
    const segments = pathname.replace(searchPath, '').split('/').filter(Boolean);

    if (segments.length > 0) {
      let accumulatedPath = searchPath;

      for (const segment of segments) {
        // Try to get the correct display name from facets (searches all facets)
        let label: string;
        if (facets) {
          const displayName = getDisplayNameFromFacets(facets, segment);
          label = displayName || capitalizeWords(decodeURIComponent(segment));
        } else {
          // Fallback to simple capitalization if no facets available
          label = capitalizeWords(decodeURIComponent(segment));
        }

        accumulatedPath = `${accumulatedPath}/${segment}`;
        crumbs.push({ href: accumulatedPath, label });
      }
    }

    return crumbs;
  }

  // Special case: For /objekt/[id]/[slug] routes in wayke and drive markets,
  // use /search breadcrumb instead of /objekt
  const isObjectDetailPage = /^\/objekt\/[^/]+(?:\/[^/]+)?$/.test(pathname);
  if (isObjectDetailPage && (market === 'wayke' || market === 'drive')) {
    const searchPath = marketMetadata.pageLinks.searchPage;
    const searchLabel = titles('/search');

    // Always add the root
    if (titles('/')) {
      crumbs.push({ href: '/', label: marketMetadata.brandName });
    }

    // Add the search breadcrumb
    if (searchLabel) {
      crumbs.push({ href: searchPath, label: searchLabel });
    }

    // Add manufacturer breadcrumb if available
    if (vehicleData?.manufacturer) {
      const manufacturerUrl = `${searchPath}/${slugify(vehicleData.manufacturer.toLowerCase(), { encode: true })}`;
      crumbs.push({ href: manufacturerUrl, label: vehicleData.manufacturer });
    }

    // Add model breadcrumb if available
    if (vehicleData?.manufacturer && vehicleData?.modelSeries) {
      const modelUrl = `${searchPath}/${slugify(vehicleData.manufacturer.toLowerCase(), { encode: true })}/${slugify(vehicleData.modelSeries.toLowerCase(), { encode: true })}`;
      crumbs.push({ href: modelUrl, label: vehicleData.modelSeries });
    }

    return crumbs;
  }

  while (true) {
    if (!path.includes('[')) {
      const generic = findGenericRoutePattern(path, marketRoutes);
      // Only add static generic routes
      if (generic && !generic.includes('[') && titles(generic)) {
        if (!crumbs.some((c) => c.href === path)) {
          crumbs.unshift({
            href: path,
            label: path === '/' ? marketMetadata.brandName : titles(generic),
          });
        }
      }
    }

    if (path === '/' || path === '') break;
    path = path.substring(0, path.lastIndexOf('/')) || '/';
  }

  // Always add the root if missing
  if (!crumbs.length && titles('/')) {
    crumbs.push({ href: '/', label: marketMetadata.brandName });
  }
  return crumbs;
};
